import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert, Button, Card, CardContent } from '@mui/material';
import { QrCodeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../api/api';
import { useTheme } from '../context/ThemeContext';

interface QRData {
  type: string;
  vendorId: string;
  tableId: string;
  tableName: string;
  sectionId: string;
  created: string;
}

interface TableInfo {
  id: string;
  name: string;
  sectionId: string;
  capacity: number;
  status: string;
}

interface SectionInfo {
  id: string;
  name: string;
  menuId?: string;
  vendorId: string;
}

interface MenuInfo {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

const QROrderHandler: React.FC = () => {
  const { vendorId, tableId } = useParams<{ vendorId: string; tableId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  
  // Extract additional parameters from URL query string
  const urlParams = new URLSearchParams(location.search);
  const sectionIdFromUrl = urlParams.get('section');
  const tableNameFromUrl = urlParams.get('name');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null);
  const [sectionInfo, setSectionInfo] = useState<SectionInfo | null>(null);
  const [menuInfo, setMenuInfo] = useState<MenuInfo | null>(null);
  const [qrData, setQrData] = useState<QRData | null>(null);

  useEffect(() => {
    handleQRScan();
  }, [vendorId, tableId]);

  const handleQRScan = async () => {
    if (!vendorId || !tableId) {
      setError('Invalid QR code: Missing vendor or table information');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Track QR scan for analytics
      await trackQRScan();
      
      // Step 1: Fetch table information
      console.log(`[QROrderHandler] Fetching table info for tableId: ${tableId}`);
      const tableResponse = await api.get(`/tables/${tableId}`);
      const table: TableInfo = tableResponse.data;
      setTableInfo(table);
      
      // Step 2: Fetch section information (use URL param if available, otherwise from table)
      const sectionId = sectionIdFromUrl || table.sectionId;
      console.log(`[QROrderHandler] Fetching section info for sectionId: ${sectionId}`);
      const sectionResponse = await api.get(`/sections/${sectionId}`);
      const section: SectionInfo = sectionResponse.data;
      setSectionInfo(section);
      
      // Step 3: Determine appropriate menu
      let menu: MenuInfo;
      let isDefaultMenu = false;
      
      if (section.menuId) {
        // Section has specific menu assigned
        console.log(`[QROrderHandler] Using section-specific menu: ${section.menuId}`);
        const menuResponse = await api.get(`/menus/${section.menuId}`);
        menu = menuResponse.data;
      } else {
        // Fallback to vendor's default menu
        console.log(`[QROrderHandler] Using vendor default menu for vendorId: ${vendorId}`);
        const vendorResponse = await api.get(`/vendors/${vendorId}`);
        const defaultMenuId = vendorResponse.data.defaultMenuId;
        
        if (!defaultMenuId) {
          throw new Error('No default menu configured for this vendor');
        }
        
        const menuResponse = await api.get(`/menus/${defaultMenuId}`);
        menu = menuResponse.data;
        isDefaultMenu = true;
      }
      
      setMenuInfo(menu);
      
      // Step 4: Create a temporary QR identifier and redirect to existing menu system
      // This allows us to leverage your existing /qr/{code}/menu API
      const qrCodeIdentifier = `TEMP-TABLE-${table.id}-${Date.now()}`;
      
      // First, register this temporary QR code with the backend
      try {
        await api.post('/qr/register-temp', {
          qrCode: qrCodeIdentifier,
          tableId: table.id,
          tableName: tableNameFromUrl || table.name,
          sectionId: section.id,
          menuId: menu.id,
          vendorId: vendorId
        });
      } catch (tempQrError) {
        console.warn('[QROrderHandler] Failed to register temp QR, using direct menu access');
      }
      
      // Redirect using existing menu system with QR parameter
      const menuPath = `/menu?qr=${qrCodeIdentifier}`;
      
      console.log(`[QROrderHandler] Redirecting to existing menu system: ${menuPath}`);
      
      // Small delay to show loading state, then redirect
      setTimeout(() => {
        window.location.href = menuPath; // Use window.location for full page reload to ensure QR system works
      }, 1000);
      
    } catch (err: any) {
      console.error('[QROrderHandler] Error processing QR scan:', err);
      
      let errorMessage = 'Failed to load menu. Please try again.';
      
      if (err.response?.status === 404) {
        if (err.config?.url?.includes('/tables/')) {
          errorMessage = 'Table not found. This QR code may be invalid or the table may have been removed.';
        } else if (err.config?.url?.includes('/sections/')) {
          errorMessage = 'Section not found. Please contact the restaurant staff.';
        } else if (err.config?.url?.includes('/menus/')) {
          errorMessage = 'Menu not found. Please contact the restaurant staff.';
        }
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. This menu may not be available at the moment.';
      } else if (err.message?.includes('default menu')) {
        errorMessage = 'No menu is configured for this restaurant. Please contact the staff.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const trackQRScan = async () => {
    try {
      // Extract QR data from URL params or location state if available
      const qrScanData = {
        vendorId,
        tableId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        source: 'qr_code',
        referrer: document.referrer
      };
      
      await api.post('/analytics/qr-scan', qrScanData);
    } catch (error) {
      console.warn('[QROrderHandler] Failed to track QR scan:', error);
      // Don't fail the main flow if analytics fail
    }
  };

  const handleRetry = () => {
    setError(null);
    handleQRScan();
  };

  const handleManualSelection = () => {
    // Redirect to vendor's main page for manual table selection
    navigate(`/vendor/${vendorId}`);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ backgroundColor: theme.background, padding: 3 }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ padding: 4 }}>
            <QrCodeIcon 
              style={{ 
                width: '64px', 
                height: '64px', 
                color: theme.primary,
                marginBottom: '16px'
              }} 
            />
            <CircularProgress 
              size={40} 
              sx={{ 
                color: theme.primary,
                marginBottom: 2
              }} 
            />
            <Typography variant="h6" sx={{ marginBottom: 1, color: theme.text }}>
              Loading Menu...
            </Typography>
            <Typography variant="body2" sx={{ color: theme.gray[600] }}>
              {tableInfo ? `Table: ${tableInfo.name}` : 'Processing QR code...'}
            </Typography>
            {sectionInfo && (
              <Typography variant="body2" sx={{ color: theme.gray[600] }}>
                Section: {sectionInfo.name}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ backgroundColor: theme.background, padding: 3 }}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ padding: 4, textAlign: 'center' }}>
            <ExclamationTriangleIcon 
              style={{ 
                width: '64px', 
                height: '64px', 
                color: theme.danger,
                marginBottom: '16px'
              }} 
            />
            <Typography variant="h6" sx={{ marginBottom: 2, color: theme.text }}>
              Unable to Load Menu
            </Typography>
            <Alert severity="error" sx={{ marginBottom: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            
            {tableInfo && (
              <Box sx={{ marginBottom: 3, padding: 2, backgroundColor: theme.gray[50], borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: theme.gray[700] }}>
                  <strong>Table:</strong> {tableInfo.name}
                </Typography>
                {sectionInfo && (
                  <Typography variant="body2" sx={{ color: theme.gray[700] }}>
                    <strong>Section:</strong> {sectionInfo.name}
                  </Typography>
                )}
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={handleRetry}
                sx={{ backgroundColor: theme.primary }}
              >
                Try Again
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleManualSelection}
                sx={{ borderColor: theme.primary, color: theme.primary }}
              >
                Select Table Manually
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // This should not render as we redirect on success
  return null;
};

export default QROrderHandler;